export const PRICE_TYPES = [
  'money',
  'black_money',
  'coin',
  'ticket',
  'hidden',
] as const

export type PriceType = (typeof PRICE_TYPES)[number]

export interface ClothingItem {
  hash: string
  collectionName: string
  gender: number
  componentType: string
  componentId: number
  drawableId: number
  textureId: number
  price: number
  type: PriceType
  size: number
  fileName: string
  previewUrl: string
}

export interface ClothingList {
  items: ClothingItem[]
  total: number
  page: number
  perPage: number
  totalPages: number
}

export interface ClothingMeta {
  collections: string[]
  componentTypes: string[]
  types: PriceType[]
  total: number
}

export interface ClothingFilters {
  collection?: string
  gender?: string
  componentType?: string
  componentId?: string
  drawableId?: string
  textureId?: string
  hash?: string
  type?: PriceType | ''
  priceMin?: number | ''
  priceMax?: number | ''
  sort?: string
  page?: number
  perPage?: number
}

export interface PricingItem {
  hash: string
  price: number
  type: PriceType
}

export interface BatchPricingResult {
  updated: number
  failed: Array<{ hash: string; error: string }>
}

export interface StatsOverview {
  total: number
  priced: number
  unpriced: number
  byGender: Record<string, number>
  byType: Record<string, number>
  byComponentType: Record<string, number>
}

export interface CollectionCoverage {
  name: string
  total: number
  priced: number
  unpriced: number
  pricedPercent: number
}

export interface StatsCoverage {
  collections: CollectionCoverage[]
}

/** Ảnh overlay / ma-nơ-canh do ender_studio chụp (không phải quần áo). */
export interface StudioImage {
  fileName: string
  kind: 'overlay' | 'mannequin'
  collectionName: string
  gender: number
  overlayId: number
  style: number
  shot?: 'face' | 'body'
  size: number
  modifiedAt: number
  previewUrl: string
}

export interface OverlayFilters {
  gender?: string
  overlayId?: string
  page?: number
  perPage?: number
}

export interface OverlayList {
  items: StudioImage[]
  total: number
  /** Tổng ảnh overlay, không tính bộ lọc. */
  overall: number
  page: number
  perPage: number
  totalPages: number
  /** Số ảnh theo overlay id (chỉ áp bộ lọc giới tính). */
  counts: Record<string, number>
}

export interface MannequinList {
  items: StudioImage[]
}

import { isMockApiEnabled, mockApi } from '@/api/mock'

const filterKeys: Record<keyof ClothingFilters, string> = {
  collection: 'collection',
  gender: 'gender',
  componentType: 'component_type',
  componentId: 'component_id',
  drawableId: 'drawable_id',
  textureId: 'texture_id',
  hash: 'hash',
  type: 'type',
  priceMin: 'price_min',
  priceMax: 'price_max',
  sort: 'sort',
  page: 'page',
  perPage: 'per_page',
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers =
    init.body || init.headers
      ? {
          ...(init.body ? { 'Content-Type': 'application/json' } : {}),
          ...init.headers,
        }
      : undefined
  const response = await fetch(path, {
    ...init,
    credentials: 'include',
    ...(headers ? { headers } : {}),
  })

  if (!response.ok) {
    let message = `Request failed (${response.status})`
    try {
      const body = (await response.json()) as { error?: string }
      message = body.error || message
    } catch {
      // Preserve the status-based fallback for non-JSON errors.
    }
    throw new Error(message)
  }

  return response.json() as Promise<T>
}

const liveApi = {
  login: (username: string, password: string) =>
    request<{ authenticated: boolean }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),
  logout: () =>
    request<{ authenticated: boolean }>('/api/auth/logout', { method: 'POST' }),
  me: () => request<{ authenticated: boolean }>('/api/auth/me'),
  listClothing: (filters: ClothingFilters = {}) => {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(filters) as Array<
      [keyof ClothingFilters, ClothingFilters[keyof ClothingFilters]]
    >) {
      if (value !== undefined && value !== '') {
        params.set(filterKeys[key], String(value))
      }
    }
    const query = params.toString()
    return request<ClothingList>(`/api/clothing${query ? `?${query}` : ''}`, {
      cache: 'no-store',
    })
  },
  clothingMeta: () => request<ClothingMeta>('/api/clothing/meta'),
  getClothing: (hash: string) =>
    request<ClothingItem>(`/api/clothing/${encodeURIComponent(hash)}`),
  patchClothing: (
    hash: string,
    body: Partial<Pick<ClothingItem, 'price' | 'type'>>
  ) =>
    request<ClothingItem>(`/api/clothing/${encodeURIComponent(hash)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  batchPricing: (items: PricingItem[]) =>
    request<BatchPricingResult>('/api/clothing/pricing/batch', {
      method: 'POST',
      body: JSON.stringify({ items }),
    }),
  deleteClothing: (hash: string) =>
    request<{ success: boolean }>(
      `/api/clothing/${encodeURIComponent(hash)}`,
      {
        method: 'DELETE',
      }
    ),
  statsOverview: () => request<StatsOverview>('/api/stats/overview'),
  statsCoverage: () => request<StatsCoverage>('/api/stats/coverage'),
  flushClothingCache: () =>
    request<{ success: boolean }>('/api/clothing/flush-cache', {
      method: 'POST',
    }),
  listOverlays: (filters: OverlayFilters = {}) => {
    const params = new URLSearchParams()
    if (filters.gender) params.set('gender', filters.gender)
    if (filters.overlayId) params.set('overlay_id', filters.overlayId)
    if (filters.page) params.set('page', String(filters.page))
    if (filters.perPage) params.set('per_page', String(filters.perPage))
    const query = params.toString()
    return request<OverlayList>(`/api/overlays${query ? `?${query}` : ''}`, {
      cache: 'no-store',
    })
  },
  listMannequins: () =>
    request<MannequinList>('/api/mannequins', { cache: 'no-store' }),
  deleteStudioImage: (fileName: string) =>
    request<{ success: boolean }>(
      `/api/studio-images/${encodeURIComponent(fileName)}`,
      { method: 'DELETE' }
    ),
}

export const api = isMockApiEnabled ? mockApi : liveApi
